import type { InfoSheetContent } from '@uiKit/components/InfoSheet.vue'
import { formatAed, formatToInternational, formatTokenPrice } from '@shared/utils'

// ⓘ explainers on the invest drawer — copy is the mobile app's, verbatim
// (investor-app offerings/invest/constants/ownershipShare.ts,
// offerings/browse/constants/pricePerToken.ts).

const PERCENT_BASE = 100

/** Captions of the "Your projected returns" tiles, as the app names them. */
export const PROJECTED_RETURN_LABELS = {
  netYield: 'Net Yield',
  appreciation: 'Estimated Appreciation',
  annualRoi: 'Estimated ROI',
} as const

export interface OwnershipSheetFigures {
  /** What the stake is worth — the VALUE row. */
  investmentValue: number
  /** The offering's Sliceal Price (purchase price). */
  slicealPrice: number | null
  /** The share the row prints, already formatted. */
  ownershipShare: string
}

/** Value ÷ Sliceal Price × 100 = Ownership Share (Figma 29018:55187). */
export const buildOwnershipShareSheet = (figures: OwnershipSheetFigures): InfoSheetContent => ({
  title: 'Ownership Share',
  note: 'The ownership value you would hold for this property',
  description:
    'Share of the property you would own based on the amount you choose to invest in this property. An investor cannot hold more than 20% ownership of a single property as per DLD regulations.',
  rows:
    figures.slicealPrice && figures.slicealPrice > 0
      ? [
          { label: 'Value', value: formatAed(figures.investmentValue) },
          { operator: '÷', label: 'Sliceal Price', value: formatAed(figures.slicealPrice) },
          { operator: '×', label: '', value: String(PERCENT_BASE) },
          {
            operator: '=',
            label: 'Ownership Share',
            value: figures.ownershipShare,
            isResult: true,
          },
        ]
      : undefined,
})

export interface TokenPriceSheetFigures {
  tokenPrice: number
  /** The offering's Sliceal Price (purchase price). */
  slicealPrice: number | null
  totalTokens: number
}

/**
 * Price Per Token + "Sliceal Price ÷ Total Tokens = Price per token"
 * (Figma 28570:22349). The app also draws Area × Tokens per sqm = Total
 * Tokens as a second boxed sum; InfoSheet holds one equation, so the
 * division — the one that prices the token — is the one shown.
 */
export const buildTokenPriceSheet = (figures: TokenPriceSheetFigures): InfoSheetContent => {
  const price = formatTokenPrice(figures.tokenPrice)
  const canDerive =
    Boolean(figures.slicealPrice && figures.slicealPrice > 0) && figures.totalTokens > 0
  return {
    title: 'Price Per Token',
    value: price,
    note: 'What one token of this property costs.',
    rows: canDerive
      ? [
          { label: 'Sliceal Price', value: formatAed(figures.slicealPrice ?? 0) },
          {
            operator: '÷',
            label: 'Total Tokens',
            value: formatToInternational(figures.totalTokens),
          },
          { operator: '=', label: 'Price per token', value: price, isResult: true },
        ]
      : undefined,
  }
}
